import { idToken } from './auth';

/**
 * Callie's voice on the screen (D34 addendum 3). Each reply is fetched as
 * speech from the API (Amazon Polly, one voice everywhere) and played; if
 * the service says voice is not set up, the device's own speech voice
 * reads it instead — a warm English female voice when the device has one.
 * Listening uses the browser's speech recognition where it exists (Chrome,
 * Safari, Edge); elsewhere the mic button simply is not shown.
 */
export type SpeakState = 'idle' | 'loading' | 'speaking';

let current: HTMLAudioElement | null = null;
let serverVoice: boolean | null = null; // null = not known yet; false = fell back to the device voice

/** Read a reply aloud. Resolves when playback ends (or immediately when nothing can speak). */
export async function speak(text: string, onState: (s: SpeakState) => void, voice?: string | null): Promise<void> {
  stop();
  onState('loading');
  if (serverVoice !== false) {
    try {
      const token = await idToken();
      const res = await fetch('/api/callie/speak', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify({ text, ...(voice ? { voice } : {}) }) });
      if (res.ok) {
        const blob = await res.blob();
        if (blob.size > 0) {
          serverVoice = true;
          await play(blob, onState);
          return;
        }
      } else if (res.status === 503) {
        serverVoice = false; // not set up on this service: use the device voice from now on
      }
    } catch {
      /* network hiccup: fall through to the device voice for this reply */
    }
  }
  await speakOnDevice(text, onState);
}

export function stop() {
  if (current) {
    current.pause();
    current.src = '';
    current = null;
  }
  try {
    window.speechSynthesis?.cancel();
  } catch {
    /* no speech on this device */
  }
}

function play(blob: Blob, onState: (s: SpeakState) => void): Promise<void> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    current = audio;
    const done = () => {
      URL.revokeObjectURL(url);
      if (current === audio) current = null;
      onState('idle');
      resolve();
    };
    audio.onended = done;
    audio.onerror = done;
    audio.onplay = () => onState('speaking');
    audio.play().catch(done); // autoplay refused: give up quietly
  });
}

/** Chrome fills the voice list asynchronously; wait briefly for it the first time. */
function deviceVoices(synth: SpeechSynthesis): Promise<SpeechSynthesisVoice[]> {
  const now = synth.getVoices();
  if (now.length) return Promise.resolve(now);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(synth.getVoices()), 400);
    synth.addEventListener('voiceschanged', () => { clearTimeout(timer); resolve(synth.getVoices()); }, { once: true });
  });
}

async function speakOnDevice(text: string, onState: (s: SpeakState) => void): Promise<void> {
  const synth = window.speechSynthesis;
  if (!synth || typeof SpeechSynthesisUtterance === 'undefined') {
    onState('idle');
    return;
  }
  const voices = await deviceVoices(synth);
  return new Promise((resolve) => {
    const u = new SpeechSynthesisUtterance(plain(text));
    const voice = pickDeviceVoice(voices);
    if (voice) u.voice = voice;
    u.rate = 1.0;
    u.pitch = 1.05;
    u.onstart = () => onState('speaking');
    u.onend = () => {
      onState('idle');
      resolve();
    };
    u.onerror = () => {
      onState('idle');
      resolve();
    };
    synth.speak(u);
  });
}

/** A warm English female voice when the device offers one; otherwise any English voice. */
export function pickDeviceVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  const english = voices.filter((v) => /^en[-_]/i.test(v.lang));
  const preferred = ['Samantha', 'Ava', 'Allison', 'Zoe', 'Karen', 'Moira', 'Tessa', 'Fiona', 'Google US English', 'Microsoft Aria', 'Microsoft Jenny', 'Microsoft Zira', 'Google UK English Female'];
  for (const name of preferred) {
    const hit = english.find((v) => v.name.startsWith(name));
    if (hit) return hit;
  }
  return english.find((v) => /female|woman/i.test(v.name)) ?? english[0] ?? null;
}

/** The device voice gets plain sentences too (the API does this for Polly). */
function plain(markdown: string): string {
  return markdown
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/^\s*(?:[-*•]|\d+[.)])\s+/gm, '')
    .replace(/\s*(?:→|->)\s*/g, ' to ')
    .replace(/\s*[·|]\s*/g, ', ')
    .replace(/\n+/g, '. ')
    .trim();
}

// ---- listening -------------------------------------------------------------------------

type Recognition = { start(): void; stop(): void; abort(): void; lang: string; interimResults: boolean; continuous: boolean; onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> & { [i: number]: { isFinal: boolean } & ArrayLike<{ transcript: string }> } }) => void) | null; onend: (() => void) | null; onerror: ((e: { error: string }) => void) | null };

export function canListen(): boolean {
  const w = window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown };
  return !!(w.SpeechRecognition || w.webkitSpeechRecognition);
}

/** Listen once: interim text as the person speaks, the final sentence when they stop. */
export function listen(onInterim: (text: string) => void, onFinal: (text: string) => void, onEnd: (error?: string) => void): () => void {
  const w = window as unknown as { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };
  const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
  if (!Ctor) {
    onEnd('no-recognition');
    return () => undefined;
  }
  const rec = new Ctor();
  rec.lang = 'en-US';
  rec.interimResults = true;
  rec.continuous = false;
  let finalText = '';
  let delivered = false;
  rec.onresult = (e) => {
    let interim = '';
    for (let i = 0; i < e.results.length; i++) {
      const r = e.results[i];
      if (r.isFinal) finalText += r[0].transcript;
      else interim += r[0].transcript;
    }
    if (interim) onInterim(finalText + interim);
    if (finalText && !interim && !delivered) {
      delivered = true;
      onFinal(finalText.trim());
    }
  };
  rec.onerror = (e) => onEnd(e.error);
  rec.onend = () => onEnd();
  try {
    rec.start();
  } catch {
    onEnd('start-failed');
  }
  return () => {
    try {
      rec.stop();
    } catch {
      /* already stopped */
    }
  };
}
